import React, { useState, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useCoreEvents, eventNames } from '@carvana/core-events';
import { getUserState } from '@carvana/core-auth';
import ConsentApi from '../../shared/apis/ConsentApi';
import { NotificationsWrapper, HeaderAlt, TitleWrapper, Title } from './PrivacySettings.styles';
import { TabContent } from '../../routes/RouterOutlet.styles';
import PageTitle from '../../shared/components/PageTitle';
import LoadingPage from '../../shared/components/LoadingPage';
import Toggle from '../NotificationSettings/ShowNotificationSettings/Toggle';
import ConsentError from './ConsentError';
import { PRIVACY_SETTINGS_PATH } from './routes/routePaths';

export const PRIVACY_SETTINGS_ROUTE = PRIVACY_SETTINGS_PATH;

const THIRD_PARTY_OPT_OUT = 'ThirdPartyOptOut';

const PRIVACY_ERROR_MESSAGE =
  'An error occurred while updating privacy settings. Please try again later.';
const PRIVACY_SUCCESS_MESSAGE =
  'Your privacy settings have been updated successfully. The page will reload to reflect the changes.';
const PRIVACY_LOADING_MESSAGE = 'Updating your preference...';

type StatusMessageProps = {
  message: string;
  type: string;
  isToggling: boolean;
  isOptedIn: boolean;
};

const StatusMessage = ({ message, type, isToggling, isOptedIn }: StatusMessageProps) => {
  // Show default status when not loading
  if (!message && type === 'default') {
    return <h4>You are opted {isOptedIn ? 'in to' : 'out of'} Third Party Sale/Share.</h4>;
  }

  // Show loading state with spinner
  if (type === 'loading' && message) {
    const spinnerStyle = {
      display: 'inline-block',
      width: '16px',
      height: '16px',
      border: '2px solid #f3f3f3',
      borderTop: '2px solid #007bff',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginRight: '8px'
    };

    return (
      <h4 style={{ color: '#007bff', display: 'flex', alignItems: 'center' }}>
        {isToggling && <span style={spinnerStyle} />}
        {message}
      </h4>
    );
  }

  // Fallback to default status
  return (
    <h4>Current Status: You are opted {isOptedIn ? 'in to' : 'out of'} Third Party Sale/Share.</h4>
  );
};

const PrivacySettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [toggleOn, setToggleOn] = useState(true);
  const [showingError, setShowingError] = useState(false);

  // New states for dynamic status feedback
  const [isToggling, setIsToggling] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('default'); // 'default', 'loading'
  const { emit } = useCoreEvents();

  const getConsentState = async () => {
    try {
      setHasError(false); // Clear any previous errors
      const { user } = getUserState();
      const userId = user?.user_id;
      const consentResp = await ConsentApi.getConsents(userId);

      // Extract the ThirdPartyOptOut consent item from the content array
      const thirdPartyOptOutConsent = consentResp.data.content.find(
        (item) => item.typeName === THIRD_PARTY_OPT_OUT
      );

      if (thirdPartyOptOutConsent) {
        // note: `isOptedIn` is true when the user has REQUESTED to be a part of a consent
        // therefore, if the user has opted out, we set `isOptedIn` to true, which sounds counterintuitive
        const allowsDataSharing = !thirdPartyOptOutConsent.isOptedIn;

        setToggleOn(allowsDataSharing);
      } else {
        // Handle case where consent item is not found
        setToggleOn(true);
      }
      // throw new Error('Consent API response is empty');
    } catch (e) {
      setHasError(true); // Set error state instead of silent fallback

      // Log error fetching consent state
      datadogRum.addAction('privacy-settings-get-consent-failure', {
        feature: 'third-party-optout',
        error: e instanceof Error ? e.message : 'Unknown error'
      });

      throw e; // Re-throw to be handled by the calling function
    }
  };

  const handleToggle = async () => {
    setIsToggling(true);
    setStatusMessage(PRIVACY_LOADING_MESSAGE);
    setStatusType('loading');

    // Optimistic update
    const newOptedOutState = !toggleOn;

    datadogRum.addAction('privacy-settings-update-consent-start', {
      action: newOptedOutState ? 'opt-out' : 'opt-in',
      feature: 'third-party-optout'
    });

    setToggleOn(newOptedOutState);

    try {
      await ConsentApi.updateCcpaOptOut();

      // Log successful user action
      datadogRum.addAction('privacy-settings-update-consent-success', {
        action: newOptedOutState ? 'opt-out' : 'opt-in',
        feature: 'third-party-optout'
      });

      // Success: Show success toast
      emit(eventNames.toasts.addToast, {
        priority: 'success',
        title: 'Preferences updated',
        message: PRIVACY_SUCCESS_MESSAGE,
        position: 'center',
        autoDismiss: true,
        duration: 5000,
        onClose: () => {
          window.location.reload(); // Reload to reflect changes
        }
      });
    } catch (error) {
      // Log failure of user action
      datadogRum.addAction('privacy-settings-update-consent-failure', {
        action: newOptedOutState ? 'opt-out' : 'opt-in',
        feature: 'third-party-optout',
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      // Revert optimistic update on error
      setToggleOn(!newOptedOutState);

      setShowingError(true);
      // Error: Show error toast
      emit(eventNames.toasts.addToast, {
        priority: 'critical',
        title: 'Error',
        message: PRIVACY_ERROR_MESSAGE,
        position: 'center',
        autoDismiss: false,
        onClose: () => {
          setShowingError(false);
        }
      });
    } finally {
      // Always return to default status
      setIsToggling(false);
      setStatusMessage('');
      setStatusType('default');
    }
  };

  const handleRetry = () => {
    setIsLoading(true);
    setHasError(false);

    // Log user-initiated retry attempt
    datadogRum.addAction('privacy-settings-load-retry', {
      feature: 'third-party-optout'
    });
    const retryGetState = async () => {
      try {
        await getConsentState();
      } catch (error) {
        // Error is already handled in getConsentState
        console.error('Failed to load consent state on retry:', error);
      } finally {
        setIsLoading(false);
      }
    };
    retryGetState();
  };

  useEffect(() => {
    const getState = async () => {
      try {
        await getConsentState();
      } catch (error) {
        // Error is already handled in getConsentState
        console.error('Failed to load consent state on mount:', error);
      } finally {
        setIsLoading(false);
      }
    };
    getState();
  }, []);

  const toggleIsDisabled = isLoading || isToggling || !toggleOn || showingError;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <PageTitle title="PRIVACY SETTINGS" className="has-subtitle" data-qa="page-title" />
      {isLoading && <LoadingPage />}
      {!isLoading && hasError && (
        <TabContent data-qa="page-content-wrap" data-testid="page-content-wrap">
          <ConsentError onRetry={handleRetry} />
        </TabContent>
      )}
      {!isLoading && !hasError && (
        <TabContent data-qa="page-content-wrap" data-testid="page-content-wrap">
          <NotificationsWrapper>
            <HeaderAlt>
              <TitleWrapper>
                <Title>Right to Opt-Out</Title>
              </TitleWrapper>
              <Toggle
                dataQa="dontsell-toggle"
                value={toggleOn}
                onClick={handleToggle}
                disabled={toggleIsDisabled}
              />
            </HeaderAlt>
            <StatusMessage
              message={statusMessage}
              type={statusType}
              isToggling={isToggling}
              isOptedIn={toggleOn}
            />
            {(toggleOn || isToggling) && (
              <>
                <p>
                  <strong>Manage Your Preferences:</strong> To opt out of the selling or sharing of
                  your personal information, simply click the toggle above or complete the Right to
                  Opt-Out Form by <a href="/privacy-policy?privacy-modal=true">clicking here</a>.
                </p>
                <br />
              </>
            )}
            <p>
              For more information about our privacy practices, please review{' '}
              <a href="/privacy-policy">Carvana's Consumer Privacy Notice</a>.
            </p>
          </NotificationsWrapper>
        </TabContent>
      )}
    </>
  );
};

export default PrivacySettings;
